import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { rolesService } from "./roles.service";

export const fetchRoles = createAsyncThunk(
    "roles/fetchAll",
    async (_, { rejectWithValue }) => {
        try {
            const response = await rolesService.getAllRoles();
            return response.roles;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to fetch roles");
        }
    }
);

export const createRole = createAsyncThunk(
    "roles/create",
    async (roleData, { rejectWithValue }) => {
        try {
            const response = await rolesService.createRole(roleData);
            return response.role;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to create role");
        }
    }
);

export const updateRole = createAsyncThunk(
    "roles/update",
    async ({ id, roleData }, { rejectWithValue }) => {
        try {
            const response = await rolesService.updateRole(id, roleData);
            return response.role;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to update role");
        }
    }
);

export const deleteRole = createAsyncThunk(
    "roles/delete",
    async (id, { rejectWithValue }) => {
        try {
            await rolesService.deleteRole(id);
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Failed to delete role");
        }
    }
);

const initialState = {
    roles: [],
    isLoading: false,
    error: null,
};

const rolesSlice = createSlice({
    name: "roles",
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch roles
            .addCase(fetchRoles.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchRoles.fulfilled, (state, action) => {
                state.isLoading = false;
                state.roles = action.payload;
            })
            .addCase(fetchRoles.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Create role
            .addCase(createRole.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createRole.fulfilled, (state, action) => {
                state.isLoading = false;
                state.roles.unshift(action.payload);
            })
            .addCase(createRole.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Update role
            .addCase(updateRole.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateRole.fulfilled, (state, action) => {
                state.isLoading = false;
                const index = state.roles.findIndex((r) => r.id === action.payload.id);
                if (index !== -1) {
                    state.roles[index] = action.payload;
                }
            })
            .addCase(updateRole.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Delete role
            .addCase(deleteRole.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteRole.fulfilled, (state, action) => {
                state.isLoading = false;
                state.roles = state.roles.filter((r) => r.id !== action.payload);
            })
            .addCase(deleteRole.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});

export const { clearError } = rolesSlice.actions;
export default rolesSlice.reducer;
